import { GONKA_CHAIN } from '@gonka-wallet/chain';

const DECIMALS = GONKA_CHAIN.denom.decimals;

export function formatGnk(ngonka: string): string {
  if (ngonka === '0') return '0';
  const pad = ngonka.padStart(DECIMALS + 1, '0');
  const whole = pad.slice(0, -DECIMALS);
  const frac = pad.slice(-DECIMALS).replace(/0+$/, '');
  return frac ? `${whole}.${frac}` : whole;
}

/**
 * Display-only formatter: 4 fractional digits (half-to-even rounded),
 * with a narrow space between every 3 whole digits. Produces values
 * like "10 994.8095" and "0.0890". Used in wallet list rows and the
 * HeroCard where precision of 9 decimals looks cluttered. The base
 * `formatGnk` is still used for Send-screen MAX/validation where full
 * precision matters.
 */
export function formatGnkDisplay(ngonka: string, decimals = 4): string {
  if (!ngonka || ngonka === '0') {
    return `0.${'0'.repeat(decimals)}`;
  }
  const pad = ngonka.padStart(DECIMALS + 1, '0');
  const whole = pad.slice(0, -DECIMALS).replace(/^0+/, '') || '0';
  const fracRaw = pad.slice(-DECIMALS);

  // Round to `decimals` with half-to-even on the trailing digits we drop.
  let wholePart = whole;
  let fracPart = fracRaw.slice(0, decimals);
  const rest = fracRaw.slice(decimals);
  if (rest.length > 0 && rest[0] && Number(rest[0]) >= 5) {
    // Round up — add 1 to fracPart as a decimal string
    let carry = 1;
    const digits = fracPart.split('').map((c) => Number(c));
    for (let i = digits.length - 1; i >= 0 && carry; i--) {
      const v = (digits[i] ?? 0) + carry;
      digits[i] = v % 10;
      carry = v >= 10 ? 1 : 0;
    }
    fracPart = digits.join('');
    if (carry) {
      // Overflow — add 1 to wholePart
      const wd = wholePart.split('').map((c) => Number(c));
      let c2 = 1;
      for (let i = wd.length - 1; i >= 0 && c2; i--) {
        const v = (wd[i] ?? 0) + c2;
        wd[i] = v % 10;
        c2 = v >= 10 ? 1 : 0;
      }
      wholePart = (c2 ? '1' : '') + wd.join('');
    }
  }

  // Insert space every 3 digits in whole part (right-to-left).
  const spaced = wholePart.replace(/\B(?=(\d{3})+(?!\d))/g, '\u202F');
  return `${spaced}.${fracPart}`;
}

export function shortGnk(ngonka: string): string {
  const formatted = Number(formatGnk(ngonka));
  if (formatted >= 1_000_000) return `${(formatted / 1_000_000).toFixed(2)}M`;
  if (formatted >= 1_000) return `${(formatted / 1_000).toFixed(1)}K`;
  return formatted.toFixed(2);
}

/**
 * Parse user-entered GNK amount (e.g. "1.5") to base-denom ngonka
 * (e.g. "1500000000") via a BigInt-safe decimal path. No Number() —
 * precision is preserved for all valid inputs. Rejects empty, negative,
 * scientific, or non-numeric. Sub-unit fractions beyond 9 decimals are
 * silently truncated; the UI should validate before calling.
 *
 * Comma is normalised to dot so users with locale-aware mobile
 * keyboards (ru, de, fr…) can type "0,1" and have it work the same as
 * "0.1".
 */
export function parseGnkToNgonka(input: string): string {
  const trimmed = input.trim().replace(',', '.');
  if (!/^\d+(\.\d+)?$/.test(trimmed)) {
    throw new Error('Invalid GNK amount');
  }
  const [wholeRaw = '0', fracRaw = ''] = trimmed.split('.');
  const paddedFrac = (fracRaw + '0'.repeat(DECIMALS)).slice(0, DECIMALS);
  const combined = (wholeRaw + paddedFrac).replace(/^0+/, '') || '0';
  return combined;
}

export function hexToBytes(hex: string): Uint8Array {
  if (hex.length % 2 !== 0) {
    throw new Error('Odd-length hex string');
  }
  const out = new Uint8Array(hex.length / 2);
  for (let i = 0; i < out.length; i++) {
    const byte = parseInt(hex.slice(i * 2, i * 2 + 2), 16);
    if (Number.isNaN(byte)) {
      throw new Error('Invalid hex character');
    }
    out[i] = byte;
  }
  return out;
}

export function bytesToHex(bytes: Uint8Array): string {
  return Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
}
